// Images
import masterCardLogo from "assets/images/logos/mastercard.png";
import visaLogo from "assets/images/logos/visa.png";

// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  Tooltip,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

// import { useAuthUser, useAuthHeader } from "react-auth-kit";

import { useEffect, useState } from "react";
import axios from "axios.js";
import { useIsAuthenticated } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import usePageTitle from "app/pageTitle";
import CompanyNotification from "app/company_notification";

function VirtualCards() {
  const pageTitle = "Virtual Cards";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  if (!isAuthenticated()) {
    navigate("/sign-in");
  }

  const [vcards, setVcards] = useState([]);
  const [refreshPage, setRefreshPage] = useState(false);

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [severity, setSeverity] = useState("error");
  const [errorMsg, setErrMsg] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [cardAlias, setCurrentCardAlias] = useState(null);
  // const [openVCardDialog, setVCardDialog] = useState(false);
  const [openNewCardDialog, setOpenNewCardDialog] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [currency, setCurrency] = useState(null);
  const [commercials, setCommercials] = useState(null);

  const [fundingBalances, setFundingBalances] = useState([]);
  const [fundingBalance, setFundingBalance] = useState(null);
  const [cardBrands, setCardBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);

  const [showCompanyNotification, setShowCompanyNotification] = useState(false);
  const [companyNotification, setCompanyNotification] = useState(null);

  const handleNewCardDialogClose = () => {
    setCurrentCardAlias(null);
    setOpenNewCardDialog(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("users/vcard/ALL/list.json")
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setVcards(result.data.data.virtual_cards);
            setCurrencies(result.data.data.currencies);
            setCommercials(result.data.data.commercials);

            if (result.data.notification) {
              setCompanyNotification(result.data.notification);
              if (result.data.notification.pages.includes("ALL")) {
                setShowCompanyNotification(true);
              } else if (result.data.notification.pages.includes("vcards")) {
                setShowCompanyNotification(true);
              }
            }
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg("An error occurred.");
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  useEffect(() => {
    if (commercials !== null && currency !== null) {
      setFundingBalance(null);
      setFundingBalances(commercials[currency].funding_balances);
      setCardBrands(commercials[currency].brands);
    }
  }, [currency]);

  async function requestCard() {
    if (currency === null || currency === "") {
      setSeverity("error");
      setErrMsg("Select a card currency");
      setOpen(true);
      return;
    }

    if (selectedBrand === null || selectedBrand === "") {
      setSeverity("error");
      setErrMsg("Select a brand for your card.");
      setOpen(true);
      return;
    }

    if (cardAlias === null || cardAlias === "") {
      setSeverity("error");
      setErrMsg("Enter a card alias/nickname");
      setOpen(true);
      return;
    }

    if (fundingBalance === null || fundingBalance === "") {
      setSeverity("error");
      setErrMsg("Select a funding balance.");
      setOpen(true);
      return;
    }

    setOpenNewCardDialog(false);
    setShowLoading(true);
    const json = {
      currency: `${currency}`,
      alias: cardAlias,
      source: fundingBalance,
      brand: selectedBrand,
    };
    await axios
      .post("users/vcard/request.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg("Card request submitted successfully. This may take a few moments to process.");
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Make sure you have an active internet connection.");
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {companyNotification !== null && showCompanyNotification ? (
        <CompanyNotification
          title={companyNotification.title}
          description={companyNotification.description}
        />
      ) : (
        <> </>
      )}
      <Dialog open={openNewCardDialog} onClose={handleNewCardDialogClose}>
        <DialogTitle>
          <MDBox>Card Request</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDTypography variant="h6" fontWeight="light">
              Select a currency and enter an alias/nickname for this card
            </MDTypography>
          </DialogContentText>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mt={3} mb={1}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Card Currency</InputLabel>
                <Select
                  label="Card Currency"
                  onChange={(e) => {
                    setCurrency(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    currencies.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      currencies.map((record) => {
                        if (record.vcard_enabled) {
                          return (
                            <MenuItem value={record.currency_code}>{record.currency_code}</MenuItem>
                          );
                        }
                        return null;
                      })
                  }
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mt={3}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Brand</InputLabel>
                <Select
                  label="Brand"
                  onChange={(e) => {
                    setSelectedBrand(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    cardBrands.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      cardBrands.map((record) => <MenuItem value={record}>{record}</MenuItem>)
                  }
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mt={2}>
              <MDInput
                name="cardAlias"
                margin="dense"
                label="Card Alias"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setCurrentCardAlias(e.target.value)}
              />
            </MDBox>
            <MDBox mt={3}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Pay From</InputLabel>
                <Select
                  label="Pay From"
                  onChange={(e) => {
                    setFundingBalance(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    fundingBalances.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      fundingBalances.map((record) => (
                        <MenuItem value={record}>{record} Balance</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
            {currency != null && selectedBrand != null ? (
              <MDBox pl={0} mt={2}>
                <Alert severity="info" sx={{ width: "100%" }}>
                  <ul>
                    <li>
                      You will be charged a{" "}
                      <b>
                        {currency} {(commercials[currency].fees[selectedBrand] / 100).toFixed(2)}
                      </b>{" "}
                      card fee.
                    </li>
                    {commercials[currency].prefund > 0 ? (
                      <li>
                        A minimum of{" "}
                        <b>
                          {currency} {(commercials[currency].prefund / 100).toFixed(2)}
                        </b>{" "}
                        is required to pre-fund your card.
                      </li>
                    ) : (
                      <> </>
                    )}
                  </ul>
                </Alert>
              </MDBox>
            ) : (
              <> </>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleNewCardDialogClose} color="dark" variant="outlined">
            Cancel
          </MDButton>
          <MDButton
            color="success"
            variant="outlined"
            onClick={() => {
              requestCard();
            }}
          >
            Proceed
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={12}>
                <Card sx={{ width: "100%", marginTop: "-5px" }}>
                  <MDBox
                    pt={2}
                    px={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <MDTypography variant="h6" fontWeight="medium">
                      My Virtual Cards
                    </MDTypography>
                    <MDButton
                      variant="contained"
                      color="dark"
                      onClick={() => {
                        // requestCard();
                        setCurrency(null);
                        setOpenNewCardDialog(true);
                      }}
                    >
                      <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                      &nbsp;request card
                    </MDButton>
                  </MDBox>
                  {vcards <= 0 ? (
                    <MDBox pt={3} pb={2} px={2}>
                      <MDBox
                        component="ul"
                        display="flex"
                        flexDirection="column"
                        p={0}
                        m={0}
                        sx={{ listStyle: "none" }}
                      >
                        <small>No virtual cards found.</small>
                      </MDBox>
                    </MDBox>
                  ) : (
                    <MDBox pt={3} pb={2} px={0}>
                      <MDBox display="flex" flexDirection="column" p={0} m={0}>
                        <Grid container spacing={3}>
                          {vcards.map((card) => (
                            <Grid item xs={12} md={6} lg={6}>
                              <MDBox
                                borderRadius="lg"
                                p={3}
                                sx={{
                                  marginLeft: "10px",
                                  marginTop: "5px",
                                  marginRight: "10px",
                                  marginBottom: "15px",
                                  border: ({ borders: { borderWidth, borderColor } }) =>
                                    `${borderWidth[1]} solid ${borderColor}`,
                                }}
                                onClick={() => {
                                  navigate(`/cards/${card.reference}`);
                                }}
                              >
                                <Grid container spacing={3}>
                                  <Grid item xs={12} lg={12} md={12}>
                                    <MDBox
                                      mb={3}
                                      sx={{ textAlign: "left" }}
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="space-between"
                                    >
                                      <MDTypography
                                        variant="h6"
                                        fontWeight="medium"
                                        textTransform="capitalize"
                                      >
                                        {card.title}
                                      </MDTypography>
                                      <MDBox lineHeight={1}>
                                        <Tooltip title="View Card" placement="top">
                                          <MDButton
                                            variant="outlined"
                                            color="info"
                                            iconOnly
                                            circular
                                            onClick={() => {
                                              navigate(`/cards/${card.reference}`);
                                            }}
                                          >
                                            <Icon
                                              sx={{ cursor: "pointer" }}
                                              fontSize="small"
                                              color="dark"
                                            >
                                              visibility
                                            </Icon>
                                          </MDButton>
                                        </Tooltip>
                                      </MDBox>
                                    </MDBox>
                                    <MDBox
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                    >
                                      <MDBox
                                        component="img"
                                        src={
                                          card.details.brand === "mastercard"
                                            ? masterCardLogo
                                            : visaLogo
                                        }
                                        alt={card.brand}
                                        width="10%"
                                        mr={2}
                                      />
                                      <MDBox
                                        lineHeight={0}
                                        sx={{ marginBottom: "15px", textAlign: "center" }}
                                      >
                                        <MDTypography variant="caption" color="text">
                                          Currency:&nbsp;
                                        </MDTypography>
                                        <MDTypography
                                          variant="caption"
                                          fontWeight="medium"
                                          textTransform="capitalize"
                                        >
                                          {card.currency.currency_code}
                                        </MDTypography>
                                      </MDBox>
                                    </MDBox>
                                    <MDBox
                                      lineHeight={1}
                                      sx={{ marginTop: "15px", textAlign: "center" }}
                                    >
                                      <MDTypography variant="h4" fontWeight="medium">
                                        {card.details.pan.substring(0, 4)}
                                        &nbsp;{"   "}&nbsp;
                                        {card.details.pan.substring(4, 8)}
                                        &nbsp;{"   "}&nbsp;
                                        {card.details.pan.substring(8, 12)}
                                        &nbsp;{"   "}&nbsp;
                                        {card.details.pan.substring(12, 16)}
                                      </MDTypography>
                                    </MDBox>
                                  </Grid>
                                  <Grid item xs={12} lg={12} md={12}>
                                    <Grid container spacing={3}>
                                      <Grid item xs={12} lg={12} md={12}>
                                        <MDBox
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="space-between"
                                        >
                                          <MDBox mr={3} lineHeight={1}>
                                            <MDTypography
                                              variant="button"
                                              fontWeight="regular"
                                              opacity={0.8}
                                            >
                                              Card Holder
                                            </MDTypography>
                                            <MDTypography
                                              variant="h6"
                                              fontWeight="medium"
                                              textTransform="capitalize"
                                            >
                                              {card.details.cardholder}
                                            </MDTypography>
                                          </MDBox>
                                          <MDBox mr={3} lineHeight={1}>
                                            <MDTypography
                                              variant="button"
                                              fontWeight="regular"
                                              opacity={0.8}
                                            >
                                              Expires
                                            </MDTypography>
                                            <MDTypography variant="h6" fontWeight="medium">
                                              {card.details.exp_date}
                                            </MDTypography>
                                          </MDBox>
                                          <MDBox lineHeight={1}>
                                            <MDTypography
                                              variant="button"
                                              fontWeight="regular"
                                              opacity={0.8}
                                            >
                                              CVV
                                            </MDTypography>
                                            <MDTypography variant="h6" fontWeight="medium">
                                              {card.details.cvv}
                                            </MDTypography>
                                          </MDBox>
                                        </MDBox>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item xs={12} lg={12} md={12}>
                                    <Grid container spacing={3}>
                                      <Grid item xs={12} lg={12} md={12}>
                                        <MDBox
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="space-between"
                                        >
                                          <MDBox mr={3} lineHeight={1}>
                                            <MDTypography
                                              variant="button"
                                              fontWeight="regular"
                                              opacity={0.8}
                                            >
                                              Billing Address
                                            </MDTypography>
                                            <MDTypography variant="h6" fontWeight="medium">
                                              {card.details.billing_address}
                                            </MDTypography>
                                          </MDBox>
                                          <MDBox lineHeight={1}>
                                            <MDTypography
                                              variant="h6"
                                              fontWeight="regular"
                                              opacity={0.8}
                                              align="right"
                                            >
                                              Postal Code
                                            </MDTypography>
                                            <MDTypography
                                              variant="h6"
                                              fontWeight="medium"
                                              align="right"
                                            >
                                              {card.details.billing_postal_code}
                                            </MDTypography>
                                          </MDBox>
                                        </MDBox>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </MDBox>
                            </Grid>
                          ))}
                        </Grid>
                      </MDBox>
                    </MDBox>
                  )}
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default VirtualCards;
